import { observable } from 'mobx';
import Flight from '@this/domain/flight/flight';
import type { FlightJson } from '@this/domain/select_repository';
import type { ETicketArgs } from '@this/domain/e_ticket/e_ticket';
import ETicket from '@this/domain/e_ticket/e_ticket';
import OrderItemCalendarEvent, {
  convertOrderItemCalendarEventResponseToArgs
} from '@this/domain/order_item_calendar_event';
import type { OrderItemCalendarEventResponseArgs } from '@this/domain/order_item_calendar_event';
import moment from '@this/lib/moment';
import type { Moment } from '@this/lib/moment';
import type { OrderItemPriceResponseArgs } from './order_item_price/order_item_price2';
import OrderItemPrice, { convertOrderItemPriceResponseToArgs } from './order_item_price/order_item_price2';
import type { OrderItemMappingResponseArgs } from './order_item_mapping2';
import OrderItemMapping, { convertOrderItemMappingResponseToArgs } from './order_item_mapping2';
import type { TravelerInformationResponseArgs } from './traveler_information';
import TravelerInformation, { convertTravelerInformationResponseToArgs } from './traveler_information';
import type { HotelElementResponseArgs } from './hotel_element2';
import type { TransportElementResponseArgs } from './transport_element2';
import HotelElement, { convertHotelElementResponseToArgs } from './hotel_element2';
import TransportElement, { convertTransportElementResponseToArgs } from './transport_element2';
import type { CancelChargeElementResponseArgs } from './cancel_charge_element2';
import CancelChargeElement, { convertCancelChargeElementResponseToArgs } from './cancel_charge_element2';
import type { ChangeFeeElementResponseArgs } from './change_fee_element2';
import ChangeFeeElement, { convertChangeFeeElementResponseToArgs } from './change_fee_element2';
import type { SupportFeeElementResponseArgs } from './support_fee_element/support_fee_element2';
import SupportFeeElement, {
  convertSupportFeeElementResponseToArgs
} from './support_fee_element/support_fee_element2';
import type { ShippingElementResponseArgs } from './shipping_element/shipping_element2';
import ShippingElement, { convertShippingElementResponseToArgs } from './shipping_element/shipping_element2';
import type { WifiElementResponseArgs } from './wifi_element/wifi_element2';
import WifiElement, { convertWifiElementResponseToArgs } from './wifi_element/wifi_element2';
import type { RoomElementResponseArgs } from './room_element/room_element2';
import RoomElement, { convertRoomElementResponseToArgs } from './room_element/room_element2';
import type { KitElementResponseArgs } from './kit_element/kit_element2';
import KitElement, { convertKitElementResponseToArgs } from './kit_element/kit_element2';
import type { TravelAuthorizationElementResponseArgs } from './travel_authorization_element2';
import TravelAuthorizationElement, {
  convertTravelAuthorizationElementResponseToArgs
} from './travel_authorization_element2';
import type { InsuranceElementResponseArgs } from './insurance_element2';
import InsuranceElement, { convertInsuranceElementResponseToArgs } from './insurance_element2';

export type Element =
  | TransportElement
  | HotelElement
  | CancelChargeElement
  | ChangeFeeElement
  | SupportFeeElement
  | ShippingElement
  | WifiElement
  | RoomElement
  | KitElement
  | TravelAuthorizationElement
  | InsuranceElement;

export type OrderItemResponseArgs = {
  id: number;
  status: number;
  job_type: string;
  order_item_category: string;
  order_item_category_text: string;
  price?: OrderItemPriceResponseArgs;
  order_item_mappings?: OrderItemMappingResponseArgs[];
  traveler_informations?: TravelerInformationResponseArgs[];
  transports?: TransportElementResponseArgs[];
  hotels?: HotelElementResponseArgs[];
  cancel_charges?: CancelChargeElementResponseArgs[];
  change_fees?: ChangeFeeElementResponseArgs[];
  support_fees?: SupportFeeElementResponseArgs[];
  shippings?: ShippingElementResponseArgs[];
  wifis?: WifiElementResponseArgs[];
  rooms?: RoomElementResponseArgs[];
  kits?: KitElementResponseArgs[];
  e_tickets?: ETicketArgs[];
  travel_authorizations?: TravelAuthorizationElementResponseArgs[];
  insurances?: InsuranceElementResponseArgs[];
  order_item_calender_events?: OrderItemCalendarEventResponseArgs[];
  sold_out?: boolean;
  flight?: FlightJson;
  first_item?: OrderItemResponseArgs;
  first_price?: OrderItemPriceResponseArgs;
  jr_auth_code?: string;
  jr_pickup_code?: string;
  is_working?: boolean;
  working_arranger_id?: number;
  work_started_at?: Moment;
  work_ended_at?: Moment;
  order_item_type?: number;
  ticketing_method?: string;
  buffer_amount?: number;
  trace_id?: string; // TODO: 型確認
};

export type OrderItemArgs = {
  id: number;
  showFee: boolean;
  status: number;
  jobType: string;
  orderItemCategory: string;
  orderItemCategoryText: string;
  price?: OrderItemPrice;
  orderItemMappings: OrderItemMapping[];
  travelerInformations: TravelerInformation[];
  transports: TransportElement[];
  hotels: HotelElement[];
  cancelCharges: CancelChargeElement[];
  changeFees: ChangeFeeElement[];
  supportFees: SupportFeeElement[];
  shippings: ShippingElement[];
  wifis: WifiElement[];
  rooms: RoomElement[];
  kits: KitElement[];
  eTickets: ETicket[];
  travelAuthorizations: TravelAuthorizationElement[];
  insurances: InsuranceElement[];
  orderItemCalendarEvents: OrderItemCalendarEvent[];
  soldOut: boolean;
  flight?: Flight;
  firstItem?: OrderItem;
  firstPrice?: OrderItemPrice;
  jrAuthCode?: string;
  jrPickupCode?: string;
  isWorking?: boolean;
  workingArrangerId?: number;
  workStartedAt?: Moment | null;
  workEndedAt?: Moment | null;
  orderItemType?: number;
  ticketingMethod?: string;
  bufferAmount: number;
  traceId?: string; // TODO: 型確認
};

export const convertOrderItemResponseToArgs = (response: OrderItemResponseArgs, showFee: boolean) => {
  const args: OrderItemArgs = {
    id: response.id,
    showFee,
    status: response.status,
    jobType: response.job_type,
    orderItemCategory: response.order_item_category,
    orderItemCategoryText: response.order_item_category_text,
    price: response.price
      ? new OrderItemPrice(convertOrderItemPriceResponseToArgs(response.price, showFee))
      : undefined,
    orderItemMappings:
      response.order_item_mappings?.map(r => new OrderItemMapping(convertOrderItemMappingResponseToArgs(r))) || [],
    travelerInformations:
      response.traveler_informations?.map(
        r => new TravelerInformation(convertTravelerInformationResponseToArgs(r))
      ) || [],
    transports:
      response.transports?.map(r => new TransportElement(convertTransportElementResponseToArgs(r))) || [],
    hotels: response.hotels?.map(r => new HotelElement(convertHotelElementResponseToArgs(r))) || [],
    cancelCharges:
      response.cancel_charges?.map(r => new CancelChargeElement(convertCancelChargeElementResponseToArgs(r))) ||
      [],
    changeFees:
      response.change_fees?.map(r => new ChangeFeeElement(convertChangeFeeElementResponseToArgs(r))) || [],
    supportFees:
      response.support_fees?.map(r => new SupportFeeElement(convertSupportFeeElementResponseToArgs(r))) || [],
    shippings: response.shippings?.map(r => new ShippingElement(convertShippingElementResponseToArgs(r))) || [],
    wifis: response.wifis?.map(r => new WifiElement(convertWifiElementResponseToArgs(r))) || [],
    rooms: response.rooms?.map(r => new RoomElement(convertRoomElementResponseToArgs(r))) || [],
    kits: response.kits?.map(r => new KitElement(convertKitElementResponseToArgs(r))) || [],
    eTickets: response.e_tickets?.map(eTicket => new ETicket(eTicket)) || [],
    travelAuthorizations:
      response.travel_authorizations?.map(
        r => new TravelAuthorizationElement(convertTravelAuthorizationElementResponseToArgs(r))
      ) || [],
    insurances:
      response.insurances?.map(r => new InsuranceElement(convertInsuranceElementResponseToArgs(r))) || [],
    orderItemCalendarEvents:
      response.order_item_calender_events?.map(
        r => new OrderItemCalendarEvent(convertOrderItemCalendarEventResponseToArgs(r))
      ) || [],
    soldOut: response.sold_out || false,
    flight: response.flight ? new Flight(response.flight) : undefined,
    firstItem: response.first_item
      ? new OrderItem(convertOrderItemResponseToArgs(response.first_item, showFee))
      : undefined,
    firstPrice: response.first_price
      ? new OrderItemPrice(convertOrderItemPriceResponseToArgs(response.first_price, showFee))
      : undefined,
    jrAuthCode: response.jr_auth_code,
    jrPickupCode: response.jr_pickup_code,
    isWorking: response.is_working,
    workingArrangerId: response.working_arranger_id,
    workStartedAt: response.work_started_at ? response.work_started_at : null,
    workEndedAt: response.work_ended_at ? response.work_ended_at : null,
    orderItemType: response.order_item_type,
    ticketingMethod: response.ticketing_method,
    bufferAmount: response.buffer_amount || 0,
    traceId: response.trace_id
  };
  return args;
};

export default class OrderItem {
  @observable id: number;
  @observable showFee: boolean;
  @observable status: number;
  @observable jobType: string;
  @observable orderItemCategory: string;
  @observable orderItemCategoryText: string;
  @observable price?: OrderItemPrice;
  @observable orderItemMappings: OrderItemMapping[];
  @observable travelerInformations: TravelerInformation[];
  @observable transports: TransportElement[];
  @observable hotels: HotelElement[];
  @observable cancelCharges: CancelChargeElement[];
  @observable changeFees: ChangeFeeElement[];
  @observable supportFees: SupportFeeElement[];
  @observable shippings: ShippingElement[];
  @observable wifis: WifiElement[];
  @observable rooms: RoomElement[];
  @observable kits: KitElement[];
  @observable eTickets: ETicket[];
  @observable travelAuthorizations: TravelAuthorizationElement[];
  @observable insurances: InsuranceElement[];
  @observable elements: Element[];
  @observable orderItemCalendarEvents: OrderItemCalendarEvent[];
  @observable soldOut: boolean;
  @observable flight?: Flight;
  @observable firstItem?: OrderItem;
  @observable firstPrice?: OrderItemPrice;
  @observable jrAuthCode?: string;
  @observable jrPickupCode?: string;
  @observable orderItemType?: number;
  @observable ticketingMethod?: string;
  @observable bufferAmount: number;
  @observable traceId?: string; // TODO: 型確認

  private travelerShinkansenSeatTypesCache?: { [key: string]: string[] };
  private travelerShinkansenTicketTypesCache?: { [key: string]: string[] };

  constructor(args: OrderItemArgs) {
    this.id = args.id;
    this.showFee = args.showFee;
    this.status = args.status;
    this.jobType = args.jobType;
    this.orderItemCategory = args.orderItemCategory;
    this.orderItemCategoryText = args.orderItemCategoryText;
    this.price = args.price;
    this.orderItemMappings = args.orderItemMappings;
    this.travelerInformations = args.travelerInformations;
    this.transports = args.transports;
    this.hotels = args.hotels;
    this.cancelCharges = args.cancelCharges;
    this.changeFees = args.changeFees;
    this.supportFees = args.supportFees;
    this.shippings = args.shippings;
    this.wifis = args.wifis;
    this.rooms = args.rooms;
    this.kits = args.kits;
    this.eTickets = args.eTickets;
    this.travelAuthorizations = args.travelAuthorizations;
    this.insurances = args.insurances;
    this.elements = [
      ...this.transports,
      ...this.hotels,
      ...this.cancelCharges,
      ...this.changeFees,
      ...this.supportFees,
      ...this.shippings,
      ...this.wifis,
      ...this.rooms,
      ...this.kits,
      ...this.travelAuthorizations,
      ...this.insurances
    ];
    this.orderItemCalendarEvents = args.orderItemCalendarEvents;
    this.soldOut = args.soldOut;
    this.flight = args.flight;
    this.firstItem = args.firstItem;
    this.firstPrice = args.firstPrice;
    this.jrAuthCode = args.jrAuthCode;
    this.jrPickupCode = args.jrPickupCode;
    this.orderItemType = args.orderItemType;
    this.ticketingMethod = args.ticketingMethod;
    this.bufferAmount = args.bufferAmount;
    this.traceId = args.traceId;
  }

  get typeStr(): string {
    switch (this.orderItemType) {
      case 0:
        return '往路';
      case 1:
        return '復路';
      case 2:
        return 'ホテル';
      case 3:
        return '航空券';
      case 4:
      case 9:
      case 10:
      case 11:
      case 12:
        return 'パッケージ';
      case 5:
        return '経路';
      case 20:
        return '出張サポート費';
      case 6:
        return '配送料';
      case 7:
        return 'WiFi';
      case 8:
        return '会議室';
      case 21:
        return '検査キット';
      case 22:
        return '変更手数料';
      case 23:
        return '査証・渡航認証関連';
      case 24:
        return '保険';
      default:
        return '';
    }
  }

  get categoryStr(): string {
    switch (this.orderItemCategory) {
      case 'hotel':
        return 'ホテル';
      case 'domestic_air':
        return '航空券';
      case 'foreign_air':
        return '航空券';
      case 'shinkansen':
        return '新幹線';
      case 'express':
        return '特急';
      case 'railway_ticket':
        return '在来線';
      case 'rental_car':
        return 'レンタカー';
      case 'wbf_package':
        return 'パッケージ';
      case 'cancel':
        return 'キャンセル料';
      case 'other':
        return 'その他';
      case 'support_fee':
        return '出張サポート費';
      case 'change_fee':
        return '変更手数料';
      case 'shipping':
        return '配送料';
      case 'wifi':
        return 'WiFi';
      case 'room':
        return '会議室';
      case 'kit':
        return '検査キット';
      case 'bus':
        return 'バス';
      case 'ana_tabisaku':
        return 'ANA旅作';
      case 'arrangement_request':
        return 'リクエストフォーム利用料';
      case 'travel_authorization':
        return '査証・渡航認証関連';
      case 'insurance':
        return '保険';
      default:
        return '';
    }
  }

  get isPriceChanged() {
    return !!(
      this.firstItem &&
      this.price &&
      this.firstItem.price &&
      this.price.totalPrice !== this.firstItem.price.totalPrice
    );
  }

  travelerShinkansenSeatTypes(elementIndex: number | null = null) {
    if (this.orderItemCategory !== 'shinkansen' && this.orderItemCategory !== 'express') {
      return [];
    }

    this.travelerShinkansenSeatTypesCache = this.travelerShinkansenSeatTypesCache || {};
    const cacheKey = elementIndex?.toString() || 'null';
    if (this.travelerShinkansenSeatTypesCache[cacheKey]) {
      return this.travelerShinkansenSeatTypesCache[cacheKey];
    }

    this.travelerShinkansenSeatTypesCache[cacheKey] = this.travelerInformations.map(travelerInformation =>
      travelerInformation.getShinkansenSeatTypeText(elementIndex)
    );
    return this.travelerShinkansenSeatTypesCache[cacheKey];
  }

  travelerShinkansenTicketTypes(elementIndex: number | null = null) {
    if (this.orderItemCategory !== 'shinkansen' && this.orderItemCategory !== 'express') {
      return [];
    }

    this.travelerShinkansenTicketTypesCache = this.travelerShinkansenTicketTypesCache || {};
    const cacheKey = elementIndex?.toString() || 'null';
    if (this.travelerShinkansenTicketTypesCache[cacheKey]) {
      return this.travelerShinkansenTicketTypesCache[cacheKey];
    }

    this.travelerShinkansenTicketTypesCache[cacheKey] = this.travelerInformations.map(travelerInformation =>
      travelerInformation.getShinkansenTicketTypeText(elementIndex)
    );
    return this.travelerShinkansenTicketTypesCache[cacheKey];
  }

  get isJrPackage() {
    return this.orderItemCategory === 'jr_package';
  }

  get isPackage() {
    return [4, 9, 10, 11, 12].includes(this.orderItemType || 0);
  }

  get isCanceled() {
    return this.jobType === 'cancel' && [7, 13].includes(this.status);
  }

  get isTicketingExpired() {
    return this.transports.some(transport => transport.isForeignAir && transport.isTicketingExpired);
  }

  get hasNotEnded() {
    const endDates = this.elements.filter(element => element?.endDate).map(element => element.endDate);

    if (endDates.length === 0) return false;

    const lastEndDate = endDates
      .sort((a, b) => (a?.toDate()?.getTime() || 0) - (b?.toDate()?.getTime() || 0))
      .pop();

    return lastEndDate ? lastEndDate.isAfter(moment()) : false;
  }

  get startDate() {
    const dates = this.getSortedDates(element => element.startDate);
    return dates ? dates[0] : null;
  }

  get endDate() {
    const dates = this.getSortedDates(element => element.endDate);
    return dates ? dates[dates.length - 1] : null;
  }

  get elementType(): string | undefined {
    const type = this.typeStr;

    if (type === 'ホテル') {
      return 'hotel';
    }

    if (['往路', '復路', '経路', '航空券'].includes(type)) {
      return 'transport';
    }

    return undefined;
  }

  travelerShinkansenTypes(elementIndex: number | null = null) {
    if (this.orderItemCategory !== 'shinkansen' && this.orderItemCategory !== 'express') return [];

    return this.travelerInformations.map(travelerInformation =>
      travelerInformation.travelerShinkansenType(elementIndex)
    );
  }

  get travelerAirlineTypes() {
    if (this.orderItemCategory !== 'domestic_air' && this.orderItemCategory !== 'foreign_air') return [];

    if (this.orderItemCategory === 'domestic_air') {
      return this.travelerInformations.map(travelerInformation => travelerInformation.travelerAirlineTypeDomestic);
    }

    return this.travelerInformations.map(travelerInformation => travelerInformation.travelerAirlineTypeForeign);
  }

  get mappingDescription() {
    const desc = this.elements[0]?.mappingDescription;
    return desc || this.categoryStr;
  }

  isFollowingElement(element: Element): boolean {
    if (!(element instanceof TransportElement) || !element.isForeignAir) {
      return false;
    }
    const firstForeignAir = this.transports.find(transport => transport.isForeignAir);
    return !!firstForeignAir && firstForeignAir.id !== element.id;
  }

  isPrecedingElement(element: Element): boolean {
    if (!(element instanceof TransportElement) || !element.isForeignAir) {
      return false;
    }
    const lastForeignAir = this.transports
      .slice()
      .reverse()
      .find(transport => transport.isForeignAir);
    return !!lastForeignAir && lastForeignAir.id !== element.id;
  }

  private getSortedDates(dateSelector: (element: Element) => moment.Moment | null): moment.Moment[] | null {
    const dates = this.elements.map(element => dateSelector(element)).filter(date => date !== null);

    if (dates.length === 0) return null;
    return dates.sort((a, b) => a.toDate().getTime() - b.toDate().getTime());
  }
}
